import axios from 'axios';

import { Constants } from '@/constants';

export interface GenerateEmbeddingsRequest {
  maximumImages?: number;
  objectKeys?: string[];
  force?: boolean;
}

export interface EmbeddingReference {
  id: string;
  source_image_id: string;
}

export interface GenerateEmbeddingsResponse {
  success: boolean;
  cigar_id: number;
  generated: number;
  skipped: number;
  references: EmbeddingReference[];
  model: string;
  version: string;
}

export interface SimilarityCandidate {
  cigar_id: number;
  confidence: number;
  similarity: number;
  supporting_references: number;
  best_similarity: number;
  mean_reference_similarity: number;
  supporting_images: number;
}

export interface FindSimilarResponse {
  success: boolean;
  matched: boolean;
  decisionReason?: string;
  candidates: SimilarityCandidate[];
}

export interface HydratedSimilarityCandidate extends SimilarityCandidate {
  cigarName?: string;
}

export const getEmbeddingSuccessMessage = (
  result: Pick<GenerateEmbeddingsResponse, 'generated' | 'skipped'>
) => {
  if (result.generated === 0 && result.skipped > 0) {
    return 'All reference images already have embeddings.';
  }
  if (result.generated === 0 && result.skipped === 0) {
    return 'No training images were found for this cigar.';
  }
  return `Generated ${result.generated} embeddings; skipped ${result.skipped} existing references.`;
};

export const generateCigarEmbeddings = async (
  cigarId: string | number,
  request: GenerateEmbeddingsRequest = { maximumImages: 10 }
) => {
  const response = await axios.post<GenerateEmbeddingsResponse>(
    `${Constants.aiPath}/admin/cigar-matching/cigars/${cigarId}/embeddings`,
    request
  );

  return response.data;
};

export const findSimilarCigars = async (imageUrls: string[]) => {
  const payload =
    imageUrls.length === 1 ? { imageUrl: imageUrls[0] } : { imageUrls };
  const response = await axios.post<FindSimilarResponse>(
    `${Constants.aiPath}/admin/cigar-matching/similar`,
    payload
  );

  return response.data;
};

export const hydrateSimilarityCandidates = async (
  candidates: SimilarityCandidate[]
): Promise<HydratedSimilarityCandidate[]> =>
  Promise.all(
    candidates.slice(0, 5).map(async (candidate) => {
      try {
        const response = await axios.get(
          `${Constants.clientPath}/cigars/${candidate.cigar_id}`
        );
        return {
          ...candidate,
          cigarName:
            response.data?.full_name ||
            [
              response.data?.brand?.name || response.data?.brand,
              response.data?.name,
            ]
              .filter(Boolean)
              .join(' '),
        };
      } catch {
        return candidate;
      }
    })
  );
